import { GasMeterDetails } from "../../types/components.types";
import {
  csvDownloadResponse,
  GasSwitchDataItem,
  MultiMprnResult,
} from "../../types/services.types";

import { flattenGasMeterDetailsForTable } from "./gaslink.utils";

// The data from the middleware is returned in nested objects.
// We need to unnest these objects in order to be able to export the entire data to CSV.
// TS type is any for now.
// Prefix is a string that will hold the path of the keys through nested objects, so we know where each value came from.
const unnestedData = (object: any, prefix = ""): Record<string, string> => {
  // Create an empty object to store the final 'flattened' result. Each key is the path to the original nested value.
  const result: Record<string, string> = {};

  // Loop through every key in a given object (from the API response).
  for (const key in object) {
    if (object.hasOwnProperty(key)) {
      // If a prefix exists, build a new key name by combining the prefix and the current key, separated by an underscore e.g.,
      // prefix = 'payload_additional_detail_0' key = 'additional_detail_msn' will result in: prefixedKey = "payload_additional_detail_0_additional_detail_msn";
      // The key will reflect the full path in the original nested object structure, so we know where each value came from.
      const prefixedKey = prefix ? `${prefix}_${key}` : key;

      // Get the value from the object using the current key.
      const value = object[key];

      // If a a value is an object/array and not null, you need to flatten it too. This is useful for objects/arrays like: additional_detail and tpr_data from the API response.
      if (typeof value === "object" && value !== null) {
        // Call the unnestedData again and pass it the nested object/array (value) and its path in 'prefixedKey' to flatten it too.
        const nestedResult = unnestedData(value, prefixedKey);

        // Merge each key-value pair from the nested result into the main result, so you can accumulate all the flattened values into one single object.
        for (const nestedKey in nestedResult) {
          if (nestedResult.hasOwnProperty(nestedKey)) {
            result[nestedKey] = nestedResult[nestedKey];
          }
        }
      } else {
        // If the value is not an object (e.g., string/number), add it to the main result using the prefixed key to preserve its original path.
        result[prefixedKey] = value;
      }
    }
  }
  return result;
};

export const triggerCsvDownload = (blob: Blob, filename: string): void => {
  // Create a downloadable URL for the blob object.
  const url = URL.createObjectURL(blob);

  // Create an anchor element (<a>) to serve as a link and initiate the CSV download once triggered.
  const link = document.createElement("a");

  // Set the href attribute to the generated URL. The <a> tag will now point to the file, making it downloadable.
  link.href = url;

  link.download = filename;

  // Append the link to the document body temporarily.
  // This is because in some old browsers, the element must be in the DOM for the click event to work.
  document.body.appendChild(link);

  // Trigger the download automatically when a user clicks on "Download CSV".
  link.click();

  // Remove the link from the DOM after the click. It prevents unused elements in the DOM.
  document.body.removeChild(link);

  // Revoke the temporary Blob URL to free up memory.
  URL.revokeObjectURL(url);
};

export const downloadGasMeterDetailsCSV = async (
  gasMeterDetails: GasMeterDetails | GasSwitchDataItem,
): Promise<csvDownloadResponse> => {
  // Get the complete data object for the current meter.
  const dataToExport = gasMeterDetails;

  try {
    const unnestedMeterDetails = Object.fromEntries(
      // Filter out empty/null/undefined values from the unnested data.
      Object.entries(unnestedData(dataToExport)).filter(
        ([_, value]) => value !== "" && value !== null && value !== undefined,
      ),
    );

    // Extract all the keys from the unnested data and join them with commas.
    // The keys will be the headers in the CSV file to provide column names.
    const headers = Object.keys(unnestedMeterDetails).join(",");

    // Extract all the values from the unnested object and format them to strings.
    // This will ensure that values that start with 0 will not be removed when exported to CSV as those values are now strings not 'numbers'.
    const values = Object.values(unnestedMeterDetails)

      // The (\t) forces excel to treat all values as strings.
      .map((value) => `\t"${String(value)}"`)

      // Join all values with commas to form a single CSV row.
      .join(",");

    // Structure the headers and values in a CSV format.
    const csvData = `${headers}\n${values}`;

    // Create a blob object containing the CSV data. Blob generates a file and provide a download link for the user without interacting with the user's server.
    const blob = new Blob([csvData], { type: "text/csv" });

    const filename = `gas_meter_details_${gasMeterDetails.mprn ?? (gasMeterDetails as GasSwitchDataItem).addressId ?? "gas_meter"}.csv`;
    triggerCsvDownload(blob, filename);
    console.log("CSV downloaded successfully");
    return { success: true };
  } catch (error) {
    console.log("Error downloading CSV:", error);
    return { success: false, error: "Failed to download meter details CSV" };
  }
};

// Helper function to escape CSV values that contain commas, quotes or new lines.
// The API returns null for some fields its types declare as strings, so null becomes an empty cell.
const escapeCSVValue = (value: string | null | undefined): string => {
  if (value === null || value === undefined) return "";
  if (value.includes(",") || value.includes('"') || value.includes("\n")) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
};

// Download multiple MPRNs to a single CSV
// Output format: Headers in row 1 (MPRN, field1, field2, ...), each MPRN record in subsequent rows.
// This allows scrolling left-right to read a single record, and up-down to find different records.
export const downloadMultiMprnCSV = (results: MultiMprnResult[]): void => {
  const tableData = flattenGasMeterDetailsForTable(results);

  // Filter out rows where all values are empty (these fields have no data for any MPRN)
  const filteredRows = tableData.rows.filter((row) =>
    row.values.some((value) => value.display !== "" && !value.isError),
  );

  // Build CSV headers: MPRN column first, followed by each field name.
  const headers = ["MPRN", ...filteredRows.map((row) => row.field)];

  // Build CSV rows: One row per MPRN (transposed from original structure).
  // Each row contains the MPRN number followed by all field values for that MPRN.
  const rows = tableData.columns.map((column, columnIndex) => {
    const rowValues = [
      column.mprn,
      ...filteredRows.map((row) => row.values[columnIndex]?.display ?? ""),
    ];
    return rowValues.map(escapeCSVValue).join(",");
  });

  // Combine headers and rows.
  const csvContent = [headers.map(escapeCSVValue).join(","), ...rows].join("\n");

  // Create blob and trigger download.
  const blob = new Blob([csvContent], { type: "text/csv" });
  const timestamp = new Date().toISOString().split("T")[0];
  const filename = `multi_mprn_search_${timestamp}.csv`;

  triggerCsvDownload(blob, filename);
};
